import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { recordsApi, organisationsApi, dashboardApi } from '../lib/api';
import { mergeSimilarPeople } from '../lib/nameMatch';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { ChevronDown, HelpCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import { RecordCard } from '../components/mylife/RecordCard';
import { TimelineView } from '../components/mylife/TimelineView';
import { RecordDetail } from '../components/mylife/RecordDetail';
import { GentleReturnPrompt } from '../components/mylife/GentleReturnPrompt';
import {
  MyLifeWalkthrough, shouldShowWalkthrough, resetWalkthrough,
} from '../components/mylife/MyLifeWalkthrough';

const VIEW_TABS = [
  { id: 'timeline', label: 'Timeline' },
  { id: 'by_org', label: 'Organisation' },
  { id: 'by_placement', label: 'Placement' },
  { id: 'by_person', label: 'Person' },
];

// Records decrypted per request while filling in the skeleton. ~8 ms each
// server-side, so a batch is well under a second; small enough that the
// cards on screen turn real quickly, large enough that the whole timeline
// fills in a handful of requests.
const DECRYPT_BATCH = 30;

export const MyLife = () => {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('timeline');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [walkthroughOpen, setWalkthroughOpen] = useState(() => shouldShowWalkthrough());
  const [gentleReturn, setGentleReturn] = useState({ open: false, orgs: [], reason: null });
  const [radarPending, setRadarPending] = useState(0);
  const [radarRunning, setRadarRunning] = useState(false);
  const [analysingCount, setAnalysingCount] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    organisation_ids: [],
    document_types: [],
    library_tags: [],
    has_redactions: false,
    multiple_sources_only: false,
    low_confidence_only: false,
    show_gaps: true,
  });

  // Records load in two steps so reading can start straight away:
  //  1. GET /records/index — the plaintext skeleton (type, organisations,
  //     year, flags) for every record, no key needed. Cards render at
  //     once, blurred, in the right year group.
  //  2. POST /records/list {ids} — decrypt in batches of DECRYPT_BATCH,
  //     whatever is on screen first (cards report themselves as they
  //     scroll into view), then the rest in the background until every
  //     card is real.
  // A failed decrypt (wrong key, tampered blob) leaves the card pending
  // rather than crashing the page; 401/422 (session expired / no key yet)
  // render as the empty state, not a red toast.
  const pendingRef = useRef(new Set());    // ids still to decrypt, not in flight
  const priorityRef = useRef([]);          // ids seen in the viewport, oldest first
  const pumpingRef = useRef(false);
  const generationRef = useRef(0);         // bumps on reload so stale batches are dropped
  const [decryptedCount, setDecryptedCount] = useState(0);

  const mergeDecrypted = useCallback((list) => {
    if (!list?.length) return;
    const byId = Object.fromEntries(list.map((r) => [r.id, r]));
    setRecords((prev) => prev.map((r) => byId[r.id] || r));
    setDecryptedCount((n) => n + list.length);
  }, []);

  const pump = useCallback(async () => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;
    const generation = generationRef.current;
    try {
      while (pendingRef.current.size > 0 && generation === generationRef.current) {
        // Viewport first, then whatever's left in display order.
        const batch = [];
        while (batch.length < DECRYPT_BATCH && priorityRef.current.length) {
          const id = priorityRef.current.shift();
          if (pendingRef.current.has(id)) batch.push(id);
        }
        for (const id of pendingRef.current) {
          if (batch.length >= DECRYPT_BATCH) break;
          if (!batch.includes(id)) batch.push(id);
        }
        batch.forEach((id) => pendingRef.current.delete(id));
        try {
          const res = await recordsApi.list(null, batch);
          if (generation !== generationRef.current) return;
          mergeDecrypted(res.data?.records || []);
        } catch (e) {
          if (generation !== generationRef.current) return;
          if (e?.response?.status && ![401, 422].includes(e.response.status)) {
            console.error('My Life: failed to decrypt a batch of records', e);
          }
          // Give up on this batch (cards stay as placeholders) but keep
          // going — one bad batch shouldn't stall the rest.
        }
      }
    } finally {
      pumpingRef.current = false;
    }
  }, [mergeDecrypted]);

  // A pending card scrolled into view: move it to the front of the queue.
  const onCardVisible = useCallback((id) => {
    if (!pendingRef.current.has(id)) return;
    if (!priorityRef.current.includes(id)) priorityRef.current.push(id);
    pump();
  }, [pump]);

  const loadRecords = useCallback(async () => {
    generationRef.current += 1;
    pendingRef.current = new Set();
    priorityRef.current = [];
    setDecryptedCount(0);
    try {
      const iRes = await recordsApi.index();
      const skeleton = iRes.data?.records || [];
      setRecords(skeleton);
      pendingRef.current = new Set(skeleton.map((r) => r.id));
      pump();
    } catch (e) {
      if (e?.response?.status && ![401, 422].includes(e.response.status)) {
        console.error('My Life: failed to load records', e);
      }
      setRecords([]);
    }
  }, [pump]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await loadRecords();

      try {
        const oRes = await organisationsApi.getAll();
        setOrganisations(oRes.data?.organisations || []);
      } catch (e) {
        console.error('My Life: failed to load organisations', e);
        setOrganisations([]);
      }

      setLoading(false);

      // Pending RADaR — best-effort, silent on failure
      try {
        const res = await recordsApi.radarPending();
        setRadarPending(res.data?.pending || 0);
      } catch {
        /* ignore */
      }

      // Documents still being analysed — explains an empty or partial
      // timeline rather than it just looking broken. Best-effort, silent.
      try {
        const res = await dashboardApi.getStats();
        setAnalysingCount(res.data?.analysing_documents || 0);
      } catch {
        /* ignore */
      }
    };
    load();
    return () => { generationRef.current += 1; };
  }, [loadRecords]);

  const pendingCount = useMemo(() => records.filter((r) => r.pending).length, [records]);

  const runRadarNow = async () => {
    if (radarRunning) return;
    setRadarRunning(true);
    try {
      const res = await recordsApi.radarRun();
      const { total = 0, success = 0, failed = 0 } = res.data || {};
      if (total === 0) {
        toast.info('Nothing to compare. No records have more than one copy waiting.');
      } else {
        toast.success(`Compared ${success} record${success === 1 ? '' : 's'}${failed ? `, ${failed} could not be compared` : ''}.`);
      }
      // Refresh records
      await loadRecords();
      setRadarPending(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Comparing the copies did not finish.');
    } finally {
      setRadarRunning(false);
    }
  };

  // Apply client-side filters
  const filtered = useMemo(() => {
    return (records || []).filter((r) => {
      // A still-locked record only knows its year; compare on that so it
      // isn't hidden by a date filter it might well match once decrypted.
      const d = r.date || (r.year ? `${r.year}-12-31` : '');
      const dLow = r.date || (r.year ? `${r.year}-01-01` : '');
      if (filters.date_from && d < filters.date_from) return false;
      if (filters.date_to && dLow > filters.date_to) return false;
      if (filters.organisation_ids.length &&
          !r.organisation_ids?.some((o) => filters.organisation_ids.includes(o))) return false;
      if (filters.document_types.length &&
          !filters.document_types.includes(r.doc_type)) return false;
      if (filters.library_tags.length &&
          !r.library_tags?.some((t) => filters.library_tags.includes(t))) return false;
      if (filters.has_redactions && !r.has_redactions) return false;
      if (filters.multiple_sources_only && (r.source_count || 0) < 2) return false;
      if (filters.low_confidence_only && !r.low_confidence) return false;
      return true;
    });
  }, [records, filters]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.date_from) n++;
    if (filters.date_to) n++;
    n += filters.organisation_ids.length;
    n += filters.document_types.length;
    n += filters.library_tags.length;
    if (filters.has_redactions) n++;
    if (filters.multiple_sources_only) n++;
    if (filters.low_confidence_only) n++;
    return n;
  }, [filters]);

  const facets = useMemo(() => {
    const docTypes = {};
    const tags = {};   // {name: count}
    const tagDisplay = {};  // {name: display_name}
    const orgs = {};
    for (const r of records) {
      if (r.doc_type) docTypes[r.doc_type] = (docTypes[r.doc_type] || 0) + 1;
      for (const t of r.library_tags || []) {
        tags[t] = (tags[t] || 0) + 1;
        if (r.library_tags_display?.[t]) tagDisplay[t] = r.library_tags_display[t];
      }
      for (const oid of r.organisation_ids || []) orgs[oid] = (orgs[oid] || 0) + 1;
    }
    return { docTypes, tags, tagDisplay, orgs };
  }, [records]);


  const resetFilters = () => setFilters({
    date_from: '', date_to: '',
    organisation_ids: [], document_types: [], library_tags: [],
    has_redactions: false, multiple_sources_only: false, low_confidence_only: false,
    show_gaps: true,
  });


  return (
    <AppShell>

      <main className="space-y-8">
        <Tabs value={view} onValueChange={setView} className="space-y-8">
          {/* Header row: title + segmented view switcher + filters */}
          <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <h1
              className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] text-ink"
              data-testid="my-life-heading"
            >
              My life
            </h1>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <TabsList aria-label="View" className="w-max">
                  {VIEW_TABS.map((v) => (
                    <TabsTrigger key={v.id} value={v.id} className="px-3 sm:px-4" data-testid={`view-tab-${v.id}`}>
                      {v.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              <div className="flex items-center gap-2 relative">
                <Button
                  variant="secondary"
                  onClick={() => setFiltersOpen((v) => !v)}
                  aria-expanded={filtersOpen}
                  data-testid="my-life-filters-btn"
                >
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="inline-flex min-w-[22px] justify-center rounded-full bg-brand px-1.5 py-0.5 text-[12px] font-semibold text-white leading-none">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
                {/* Not a modal — no overlay, doesn't trap focus or block the
                    page. It's meant to stay open while you scroll the
                    timeline and come back to adjust filters, so it only
                    closes when you ask it to (this button or the X below). */}
                {filtersOpen && (
                  <div
                    className="fixed sm:absolute inset-x-0 sm:inset-x-auto top-[64px] sm:top-full sm:right-0 sm:mt-2 z-40 sm:w-[420px] max-h-[calc(100vh-80px)] overflow-y-auto rounded-lg bg-surface shadow-overlay border border-rule p-5"
                    data-testid="my-life-filters-panel"
                  >
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div>
                        <h2 className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink">Filters</h2>
                        <p className="text-ink-55 text-sm mt-0.5">
                          Narrow your records by date, organisation, type or theme.
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setFiltersOpen(false)}
                        aria-label="Close filters"
                        data-testid="my-life-filters-close"
                      >
                        <X className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </div>
                    <FilterPanel
                      filters={filters}
                      setFilters={setFilters}
                      facets={facets}
                      organisations={organisations}
                      onReset={resetFilters}
                    />
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { resetWalkthrough(); setWalkthroughOpen(true); }}
                  aria-label="Read the introduction again"
                  title="Read the introduction again"
                  data-testid="my-life-replay-intro"
                >
                  <HelpCircle aria-hidden="true" />
                </Button>
              </div>
            </div>
          </header>

          {/* RADaR pending prompt */}
          {radarPending > 0 && (
            <div
              className="rounded-lg bg-ground p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              data-testid="radar-pending-banner"
            >
              <div className="flex-1 min-w-0 space-y-1">
                <p className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">
                  {radarPending} record{radarPending === 1 ? ' has' : 's have'} more than one copy
                </p>
                <p className="text-[15px] text-ink-70 leading-relaxed">
                  Comparing the copies can recover text that one organisation blacked out, and shows where they disagree.
                </p>
              </div>
              <Button
                onClick={runRadarNow}
                disabled={radarRunning}
                data-testid="run-radar-btn"
              >
                {radarRunning ? (
                  <><Loader2 className="animate-spin" aria-hidden="true" /> Comparing…</>
                ) : (
                  'Compare copies'
                )}
              </Button>
            </div>
          )}

          {!loading && pendingCount > 0 && (
            <p
              className="pd-mono text-ink-55 flex items-center gap-2"
              role="status"
              aria-live="polite"
              data-testid="my-life-unlocking"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              Unlocking {decryptedCount} of {records.length} records — you can start reading
            </p>
          )}

          {!loading && records.length > 0 && analysingCount > 0 && (
            <p
              className="pd-mono text-ink-55 flex items-center gap-2"
              role="status"
              aria-live="polite"
              data-testid="my-life-analysing"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              Analysing {analysingCount} more document{analysingCount === 1 ? '' : 's'} — more records will appear as they finish
            </p>
          )}

          {loading ? (
            <div className="rounded-lg bg-ground p-12 flex items-center justify-center text-ink-55 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Loading your records…
            </div>
          ) : records.length === 0 && analysingCount > 0 ? (
            <EmptyState
              heading="Still analysing your first documents"
              body="We're reading through what's arrived — dates, people and redactions are pulled out automatically. Your life records will appear here as soon as it's done, usually a minute or two per document."
              testId="my-life-analysing-empty"
              loading
            />
          ) : records.length === 0 ? (
            <EmptyState
              heading="Nothing here yet"
              body="Records appear once an organisation replies and their files have been analysed. The first reply usually takes three to four weeks."
              action="See your requests"
              onAction={() => navigate('/sar')}
              testId="my-life-empty"
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              heading="No records match"
              body="Nothing matches these filters. Clear them to see every record again."
              action="Clear filters"
              onAction={resetFilters}
              testId="my-life-filtered-empty"
            />
          ) : (
            <>
              <TabsContent value="timeline" className="mt-0">
                <TimelineView
                  records={filtered}
                  showGaps={filters.show_gaps}
                  onSelect={setSelectedRecordId}
                  onVisible={onCardVisible}
                />
              </TabsContent>
              <TabsContent value="by_org" className="mt-0">
                <ByOrgView records={filtered} organisations={organisations} onSelect={setSelectedRecordId} onVisible={onCardVisible} />
              </TabsContent>
              <TabsContent value="by_placement" className="mt-0">
                <ByPlacementView records={filtered} organisations={organisations} onSelect={setSelectedRecordId} onVisible={onCardVisible} onGoToOrganisations={() => navigate('/organisations')} />
              </TabsContent>
              <TabsContent value="by_person" className="mt-0">
                <ByPersonView records={filtered} onSelect={setSelectedRecordId} onVisible={onCardVisible} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>

      <RecordDetail
        recordId={selectedRecordId}
        open={!!selectedRecordId}
        onOpenChange={(o) => !o && setSelectedRecordId(null)}
        onOpenDocument={(docId, page) => navigate(`/documents?open=${docId}${page ? `&page=${page}` : ''}`)}
        onRunRadar={radarPending > 0 ? runRadarNow : null}
        onClose={({ durationMs, sensitive, orgs }) => {
          // Show the gentle return prompt when the record was either sensitive
          // or the user sat with it for >3 minutes.
          const LONG_READ_MS = 3 * 60 * 1000;
          if (sensitive) {
            setGentleReturn({ open: true, orgs, reason: 'sensitive' });
          } else if (durationMs >= LONG_READ_MS) {
            setGentleReturn({ open: true, orgs: [], reason: 'long_read' });
          }
        }}
      />

      <GentleReturnPrompt
        open={gentleReturn.open}
        orgs={gentleReturn.orgs}
        reason={gentleReturn.reason}
        onClose={() => setGentleReturn((p) => ({ ...p, open: false }))}
      />

      <MyLifeWalkthrough
        open={walkthroughOpen}
        onOpenChange={setWalkthroughOpen}
        onReady={() => setWalkthroughOpen(false)}
      />

    </AppShell>
  );
};


// ---------------------------------------------------------------------------
// Filter panel
// ---------------------------------------------------------------------------
const FilterLabel = ({ children }) => (
  <p className="pd-eyebrow">{children}</p>
);

const FilterPanel = ({ filters, setFilters, facets, organisations, onReset }) => {
  const update = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const toggleArrayItem = (k, v) => {
    const arr = filters[k] || [];
    if (arr.includes(v)) update(k, arr.filter((x) => x !== v));
    else update(k, [...arr, v]);
  };

  const sortedDocTypes = Object.entries(facets.docTypes).sort((a, b) => b[1] - a[1]);
  const sortedTags = Object.entries(facets.tags).sort((a, b) => b[1] - a[1]).slice(0, 20);
  const relevantOrgs = organisations.filter((o) => facets.orgs[o.id]);

  return (
    <div className="space-y-7 py-6">
      <section className="space-y-2.5">
        <FilterLabel>Between which dates?</FilterLabel>
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" aria-label="From" value={filters.date_from} onChange={(e) => update('date_from', e.target.value)} data-testid="filter-date-from" />
          <Input type="date" aria-label="To" value={filters.date_to} onChange={(e) => update('date_to', e.target.value)} data-testid="filter-date-to" />
        </div>
      </section>

      {relevantOrgs.length > 0 && (
        <section className="space-y-2.5">
          <FilterLabel>Organisations</FilterLabel>
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {relevantOrgs.map((o) => (
              <label key={o.id} className="flex items-center gap-3 text-[15px] cursor-pointer min-h-[32px]">
                <Checkbox
                  checked={filters.organisation_ids.includes(o.id)}
                  onCheckedChange={() => toggleArrayItem('organisation_ids', o.id)}
                />
                <span className="flex-1 truncate text-ink">{o.name}</span>
                <span className="text-sm text-ink-55">{facets.orgs[o.id]}</span>
              </label>
            ))}
          </div>
        </section>
      )}

      {sortedDocTypes.length > 0 && (
        <section className="space-y-2.5">
          <FilterLabel>Document types</FilterLabel>
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {sortedDocTypes.map(([name, count]) => (
              <label key={name} className="flex items-center gap-3 text-[15px] cursor-pointer min-h-[32px]">
                <Checkbox
                  checked={filters.document_types.includes(name)}
                  onCheckedChange={() => toggleArrayItem('document_types', name)}
                />
                <span className="flex-1 truncate text-ink">{name}</span>
                <span className="text-sm text-ink-55">{count}</span>
              </label>
            ))}
          </div>
        </section>
      )}

      {sortedTags.length > 0 && (
        <section className="space-y-2.5">
          <FilterLabel>Themes</FilterLabel>
          <div className="flex flex-wrap gap-2">
            {sortedTags.map(([name, count]) => {
              const on = filters.library_tags.includes(name);
              const display = facets.tagDisplay?.[name] || name;
              return (
                <button
                  key={name}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleArrayItem('library_tags', name)}
                  className={`text-[13px] font-medium px-[11px] py-1.5 rounded-full transition-colors duration-state ${
                    on ? 'bg-brand-wash text-brand-ink' : 'bg-inset text-ink-70 hover:bg-rule'
                  }`}
                >
                  {display} ({count})
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="space-y-2.5">
        <FilterLabel>Only show</FilterLabel>
        <div className="space-y-2">
          <label className="flex items-center gap-3 text-[15px] cursor-pointer min-h-[32px]">
            <Checkbox checked={filters.has_redactions} onCheckedChange={(v) => update('has_redactions', !!v)} data-testid="filter-has-redactions" />
            <span>Records with blacked-out text</span>
          </label>
          <label className="flex items-center gap-3 text-[15px] cursor-pointer min-h-[32px]">
            <Checkbox checked={filters.multiple_sources_only} onCheckedChange={(v) => update('multiple_sources_only', !!v)} data-testid="filter-multiple-sources" />
            <span>Records with more than one copy</span>
          </label>
          <label className="flex items-center gap-3 text-[15px] cursor-pointer min-h-[32px]">
            <Checkbox checked={filters.low_confidence_only} onCheckedChange={(v) => update('low_confidence_only', !!v)} data-testid="filter-low-confidence" />
            <span>Records whose details need checking</span>
          </label>
          <label className="flex items-center gap-3 text-[15px] cursor-pointer min-h-[32px]">
            <Checkbox checked={filters.show_gaps} onCheckedChange={(v) => update('show_gaps', !!v)} data-testid="filter-show-gaps" />
            <span>Mark gaps with no records on the timeline</span>
          </label>
        </div>
      </section>

      <div className="pt-4 border-t border-rule">
        <Button variant="secondary" onClick={onReset} className="w-full" data-testid="filter-reset">
          Clear all filters
        </Button>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Timeline view
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// By Organisation view
// ---------------------------------------------------------------------------
const ByOrgView = ({ records, organisations, onSelect, onVisible }) => {
  const orgMap = useMemo(() => Object.fromEntries(organisations.map((o) => [o.id, o])), [organisations]);
  const groups = useMemo(() => {
    const byOrg = {};
    const unknown = [];
    for (const r of records) {
      if (!r.organisation_ids?.length) {
        unknown.push(r);
        continue;
      }
      for (const oid of r.organisation_ids) {
        byOrg[oid] = byOrg[oid] || [];
        byOrg[oid].push(r);
      }
    }
    const out = Object.entries(byOrg)
      .map(([oid, recs]) => ({
        id: oid,
        name: orgMap[oid]?.name || 'Unknown',
        events: recs.filter((r) => r.is_event !== false),
        general: recs.filter((r) => r.is_event === false),
        mentions: aggregateMentions(recs),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (unknown.length) {
      out.push({
        id: null,
        name: 'Source unknown',
        events: unknown.filter((r) => r.is_event !== false),
        general: unknown.filter((r) => r.is_event === false),
        mentions: aggregateMentions(unknown),
      });
    }
    return out;
  }, [records, orgMap]);

  return (
    <div className="space-y-10">
      {groups.map((g) => (
        <div key={g.id || 'unknown'} className="space-y-4">
          <CollapsibleGroup title={g.name} count={g.events.length}>
            {g.events.length > 0 && (
              <div className="space-y-2.5">
                {g.events.map((r) => <RecordCard key={r.id} record={r} onClick={() => onSelect(r.id)} onVisible={onVisible} />)}
              </div>
            )}

            {/* "General information" (non-event records — bulk admin
                exports, etc.) is deliberately hidden for now — asked for
                originally, then explicitly de-scoped: "for now we're going
                to focus on events, letters, reports, placement moves,
                ignore the general information". Kept in the data model
                (is_event) and this component (g.general) so it's a one-line
                change to bring back once there's an actual design for it,
                not a re-build. */}

            <MentionsPanel mentions={g.mentions} />
          </CollapsibleGroup>
        </div>
      ))}
    </div>
  );
};

/** People and organisations named across a set of records, deduplicated. */
function aggregateMentions(records) {
  const people = new Map(); // lowercased name -> { name, roles: Set }
  const orgs = new Set();
  for (const r of records) {
    for (const p of r.people || []) {
      const name = (p?.name || '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const entry = people.get(key) || { name, roles: new Set() };
      if (p.role) entry.roles.add(p.role);
      people.set(key, entry);
    }
    for (const o of r.organisations_mentioned || []) {
      const name = (o || '').trim();
      if (name) orgs.add(name);
    }
  }
  // Exact matches (case-insensitive) are merged above; this second pass
  // catches the same person extracted under different forms across
  // mentions — "K Hutchison" on one page, "Kim Hutchison" on another.
  const merged = mergeSimilarPeople(
    [...people.values()].map((e) => ({ name: e.name, roles: e.roles })),
    (kept, incoming) => ({ name: kept.name, roles: new Set([...kept.roles, ...incoming.roles]) }),
  );
  return {
    people: merged
      .map(({ name, roles }) => ({ name, roles: [...roles] }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    organisations: [...orgs].sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * Surfaces the people/organisations named in this org's records — a first
 * step towards suggesting contacts and new organisations to add. Purely a
 * read-only list for now: it doesn't yet write anything back, so nothing
 * here is added to your organisations/contacts without you doing it
 * yourself elsewhere.
 */
const MentionsPanel = ({ mentions }) => {
  if (!mentions.people.length && !mentions.organisations.length) return null;
  return (
    <div className="rounded-lg bg-ground p-4 mt-2 space-y-3" data-testid="mentions-panel">
      {mentions.people.length > 0 && (
        <div>
          <p className="pd-eyebrow text-ink-55 mb-1.5">People mentioned</p>
          <div className="flex flex-wrap gap-1.5">
            {mentions.people.map((p) => (
              <span
                key={p.name}
                className="inline-flex items-center rounded-full bg-surface px-[11px] py-1 text-[13px] font-medium text-ink-70"
                title={p.roles.join(', ') || undefined}
              >
                {p.name}{p.roles.length > 0 && <span className="text-ink-55">&nbsp;· {p.roles[0]}</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      {mentions.organisations.length > 0 && (
        <div>
          <p className="pd-eyebrow text-ink-55 mb-1.5">Organisations mentioned</p>
          <div className="flex flex-wrap gap-1.5">
            {mentions.organisations.map((name) => (
              <span
                key={name}
                className="inline-flex items-center rounded-full bg-surface px-[11px] py-1 text-[13px] font-medium text-ink-70"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// By Placement view
// ---------------------------------------------------------------------------
const ByPlacementView = ({ records, organisations, onSelect, onVisible, onGoToOrganisations }) => {
  const placements = useMemo(() => {
    const out = [];
    for (const o of organisations) {
      for (const p of o.placements || []) {
        const startDate = p.start_date || o.start_date;
        const endDate = p.end_date || o.end_date;
        if (!startDate) continue;
        out.push({
          id: p.id,
          name: p.name || `${p.type || 'Placement'}`,
          orgName: o.name,
          startDate,
          endDate,
        });
      }
    }
    return out.sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  }, [organisations]);

  const groups = useMemo(() => {
    const ungrouped = [];
    const groupsByPlacement = placements.map((p) => ({ placement: p, records: [] }));
    const placementMap = Object.fromEntries(groupsByPlacement.map((g) => [g.placement.id, g]));
    for (const r of records) {
      if (r.placement_id && placementMap[r.placement_id]) {
        placementMap[r.placement_id].records.push(r);
      } else {
        ungrouped.push(r);
      }
    }
    return { groupsByPlacement: groupsByPlacement.filter((g) => g.records.length), ungrouped };
  }, [records, placements]);

  if (placements.length === 0) {
    return (
      <EmptyState
        heading="No placements yet"
        body="Add the homes and placements you lived in to each organisation, and your records will be grouped under them here."
        action="Go to organisations"
        onAction={onGoToOrganisations}
      />
    );
  }

  return (
    <div className="space-y-8">
      {groups.groupsByPlacement.map((g) => (
        <CollapsibleGroup
          key={g.placement.id}
          title={g.placement.name}
          subtitle={`${g.placement.startDate || '?'} to ${g.placement.endDate || 'present'} · ${g.placement.orgName}`}
          count={g.records.length}
        >
          {g.records.map((r) => <RecordCard key={r.id} record={r} onClick={() => onSelect(r.id)} onVisible={onVisible} />)}
        </CollapsibleGroup>
      ))}
      {groups.ungrouped.length > 0 && (
        <CollapsibleGroup title="Outside any known placement" count={groups.ungrouped.length}>
          {groups.ungrouped.map((r) => <RecordCard key={r.id} record={r} onClick={() => onSelect(r.id)} onVisible={onVisible} />)}
        </CollapsibleGroup>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// By Person view — uses label if possible; real entity people requires detail load
// ---------------------------------------------------------------------------
const ByPersonView = ({ records, onSelect, onVisible }) => {
  // Real entity data now, not a guess from the label: every person named in
  // a record (people mentioned, plus whoever wrote it — a form tutor's
  // school report and a school report that just mentions them in passing
  // both count, but the "Wrote this" tag tells them apart).
  const groups = useMemo(() => {
    const map = new Map(); // lowercased name -> { name, records: Map(id -> {record, wrote}) }
    const noOne = [];
    for (const r of records) {
      const people = r.people || [];
      const author = (r.author || '').trim();
      const entries = people
        .map((p) => ({ name: (p?.name || '').trim(), role: p?.role || '' }))
        .filter((p) => p.name);
      if (author && !entries.some((p) => p.name.toLowerCase() === author.toLowerCase())) {
        entries.push({ name: author, role: '' });
      }
      if (entries.length === 0) {
        noOne.push(r);
        continue;
      }
      for (const { name, role } of entries) {
        const key = name.toLowerCase();
        const wrote = author && name.toLowerCase() === author.toLowerCase();
        const group = map.get(key) || { name, role, records: new Map() };
        if (!group.role && role) group.role = role;
        group.records.set(r.id, { record: r, wrote });
        map.set(key, group);
      }
    }
    // Merge the same person extracted under different forms across
    // mentions ("K Hutchison" vs "Kim Hutchison") before deciding who has
    // enough records to get their own group.
    const mergedGroups = mergeSimilarPeople(
      [...map.values()],
      (kept, incoming) => ({
        name: kept.name,
        role: kept.role || incoming.role,
        records: new Map([...kept.records, ...incoming.records]),
      }),
    );
    const groupsArr = mergedGroups
      .map((g) => ({ name: g.name, role: g.role, entries: [...g.records.values()] }))
      .sort((a, b) => b.entries.length - a.entries.length);
    // People with only 1 record → Others
    const major = groupsArr.filter((g) => g.entries.length >= 2);
    const minor = groupsArr.filter((g) => g.entries.length < 2).flatMap((g) => g.entries.map((e) => e.record));
    return { major, others: [...minor, ...noOne] };
  }, [records]);

  return (
    <div className="space-y-8">
      {groups.major.length === 0 && (
        <p className="text-[16px] text-ink-55 leading-relaxed max-w-[60ch]">
          No one appears in more than one record yet. People are grouped here once they show up across your records.
        </p>
      )}
      {groups.major.map((g) => (
        <CollapsibleGroup
          key={g.name}
          title={g.name}
          subtitle={g.role || undefined}
          count={g.entries.length}
        >
          {g.entries.map(({ record: r, wrote }) => (
            <div key={r.id} className="relative">
              <RecordCard record={r} onClick={() => onSelect(r.id)} onVisible={onVisible} />
              {wrote && (
                <span className="absolute top-4 right-5 text-[13px] font-medium text-ink-55">Wrote this</span>
              )}
            </div>
          ))}
        </CollapsibleGroup>
      ))}
      {groups.others.length > 0 && (
        <CollapsibleGroup title="Everyone else" count={groups.others.length}>
          {groups.others.map((r) => <RecordCard key={r.id} record={r} onClick={() => onSelect(r.id)} onVisible={onVisible} />)}
        </CollapsibleGroup>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Collapsible group wrapper (used by By Org / By Placement / By Person)
// ---------------------------------------------------------------------------
const CollapsibleGroup = ({ title, subtitle, count, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center justify-between gap-3 w-full text-left rounded-md py-1"
      >
        <div className="min-w-0">
          <h3 className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink">{title}</h3>
          {subtitle && <p className="pd-mono text-ink-55 mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2 text-sm text-ink-55 flex-shrink-0">
          <span>{count} record{count === 1 ? '' : 's'}</span>
          <ChevronDown className={`w-4 h-4 stroke-[1.5] transition-transform duration-state ${open ? '' : '-rotate-90'}`} aria-hidden="true" />
        </div>
      </button>
      {open && <div className="flex flex-col gap-2.5 mt-3">{children}</div>}
    </section>
  );
};


// ---------------------------------------------------------------------------
// Empty states: placeholder square → heading → one sentence on *when* → one secondary button
// ---------------------------------------------------------------------------
const EmptyState = ({ heading, body, action, onAction, testId, loading }) => (
  <div className="py-10 sm:py-14 flex flex-col items-start gap-5 max-w-[52ch]" data-testid={testId}>
    {loading ? (
      <div className="w-[52px] h-[52px] rounded-lg bg-brand-wash flex items-center justify-center animate-pulse" aria-hidden="true">
        <Loader2 className="w-6 h-6 text-brand animate-spin" />
      </div>
    ) : (
      <div className="w-[52px] h-[52px] rounded-lg bg-inset" aria-hidden="true" />
    )}
    <div className="space-y-2">
      <h2 className="font-heading text-[26px] font-semibold tracking-[-0.02em] leading-[1.15] text-ink">
        {heading}
      </h2>
      <p className="text-[16px] text-ink-70 leading-relaxed">{body}</p>
    </div>
    {action && (
      <Button variant="secondary" onClick={onAction}>
        {action}
      </Button>
    )}
  </div>
);

export default MyLife;
